import { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { Restaurant } from '../types';

export interface MapViewHandle {
  focusRestaurant: (lat: number, lng: number) => void;
}

interface MapViewProps {
  restaurants: Restaurant[];
  searchPoint: { lat: number; lng: number };
}

/**
 * MapView — Leaflet map component.
 * Uses dynamic import to avoid SSR issues and keep bundle lightweight.
 * Renders markers for restaurants and the search point.
 * Supports fullscreen toggle.
 */
const MapView = forwardRef<MapViewHandle, MapViewProps>(function MapView({ restaurants, searchPoint }, ref) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const prevSearchPointRef = useRef<{ lat: number; lng: number } | null>(null);
  const prevRestaurantIdsRef = useRef<string>('');
  const openPopupIdRef = useRef<string | null>(null);

  useImperativeHandle(ref, () => ({
    focusRestaurant(lat: number, lng: number) {
      const map = mapInstanceRef.current;
      if (!map) return;
      map.setView([lat, lng], 16, { animate: true, duration: 0.5 });
    },
  }), []);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  // Handle Escape key to exit fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isFullscreen]);

  // Invalidate map size when fullscreen toggles
  useEffect(() => {
    if (mapInstanceRef.current) {
      setTimeout(() => mapInstanceRef.current?.invalidateSize(), 100);
    }
  }, [isFullscreen]);

  // Initialize map
  useEffect(() => {
    let mounted = true;

    async function initMap() {
      const L = await import('leaflet');
      await import('leaflet/dist/leaflet.css');

      if (!mounted || !mapRef.current || mapInstanceRef.current) return;

      const map = L.map(mapRef.current, {
        closePopupOnClick: false,
      }).setView(
        [searchPoint.lat, searchPoint.lng],
        13
      );

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
      }).addTo(map);

      mapInstanceRef.current = map;
      markersRef.current = L.layerGroup().addTo(map);
      setMapReady(true);
    }

    initMap();

    return () => {
      mounted = false;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Update markers when restaurants change OR map becomes ready
  useEffect(() => {
    if (!mapReady) return;

    // Skip update if restaurant list hasn't meaningfully changed (same IDs in same order)
    const limited = restaurants.slice(0, 50);
    const currentIds = limited.map((r) => r.restaurantId).join(',');
    const searchPointChanged = !prevSearchPointRef.current ||
      prevSearchPointRef.current.lat !== searchPoint.lat ||
      prevSearchPointRef.current.lng !== searchPoint.lng;

    if (currentIds === prevRestaurantIdsRef.current && !searchPointChanged) {
      return; // No meaningful change, keep existing markers & popups intact
    }
    prevRestaurantIdsRef.current = currentIds;

    async function updateMarkers() {
      const L = await import('leaflet');
      const markers = markersRef.current;
      const map = mapInstanceRef.current;
      if (!markers || !map) return;

      // Remember which popup is currently open
      const openPopupRestaurantId: string | null = openPopupIdRef.current;

      markers.clearLayers();

      // Search point marker (blue)
      const searchIcon = L.divIcon({
        className: 'search-marker',
        html: '<div style="width:16px;height:16px;background:#1976d2;border-radius:50%;border:3px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.3)"></div>',
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      });

      L.marker([searchPoint.lat, searchPoint.lng], { icon: searchIcon })
        .bindPopup('Hakupiste')
        .addTo(markers);

      // Restaurant markers (green, max 50)
      const restaurantIcon = L.divIcon({
        className: 'restaurant-marker',
        html: '<div style="width:12px;height:12px;background:#2e7d32;border-radius:50%;border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.3)"></div>',
        iconSize: [12, 12],
        iconAnchor: [6, 6],
      });

      for (const r of limited) {
        const popupContent = buildPopup(r);
        const marker = L.marker([r.coordinates.lat, r.coordinates.lng], { icon: restaurantIcon })
          .bindPopup(popupContent, { autoPanPadding: L.point(40, 40) })
          .addTo(markers);

        // Track popup open/close state
        marker.on('popupopen', () => { openPopupIdRef.current = r.restaurantId; });
        marker.on('popupclose', () => {
          if (openPopupIdRef.current === r.restaurantId) {
            openPopupIdRef.current = null;
          }
        });

        // Re-open popup if it was open before the update
        if (openPopupRestaurantId === r.restaurantId) {
          marker.openPopup();
        }
      }

      // Only fit bounds when searchPoint changes or on first load
      if (searchPointChanged) {
        prevSearchPointRef.current = { ...searchPoint };

        if (limited.length > 0) {
          const bounds = L.latLngBounds([
            [searchPoint.lat, searchPoint.lng],
            ...limited.map((r) => [r.coordinates.lat, r.coordinates.lng] as [number, number]),
          ]);
          map.fitBounds(bounds, { padding: [30, 30] });
        } else {
          map.setView([searchPoint.lat, searchPoint.lng], 13, { animate: true });
        }
      }
    }

    updateMarkers();
  }, [restaurants, searchPoint, mapReady]);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: isFullscreen ? 'fixed' : 'relative',
        inset: isFullscreen ? 0 : 'auto',
        zIndex: isFullscreen ? 1300 : 'auto',
        width: '100%',
        height: '100%',
        minHeight: 300,
        borderRadius: isFullscreen ? 0 : 1,
        overflow: 'hidden',
        transition: 'all 0.3s ease',
      }}
    >
      <Box
        ref={mapRef}
        sx={{
          width: '100%',
          height: '100%',
        }}
      />
      <IconButton
        onClick={toggleFullscreen}
        aria-label={isFullscreen ? 'Sulje kokoruutu' : 'Kokoruutu'}
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          zIndex: 1000,
          bgcolor: 'background.paper',
          boxShadow: 2,
          '&:hover': { bgcolor: 'grey.100' },
        }}
      >
        {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
      </IconButton>
    </Box>
  );
});

export default MapView;

function buildPopup(r: Restaurant): string {
  const distance = r.distance !== undefined
    ? `${r.distance.toFixed(1).replace('.', ',')} km`
    : 'ei saatavilla';
  const price = r.price !== null
    ? `${r.price.toFixed(2).replace('.', ',')} €`
    : 'ei saatavilla';
  const items = r.lunchItems && r.lunchItems.length > 0
    ? (() => {
        // Show only today's lunch in popup
        const today = new Date();
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
        const todayItems = r.lunchItems!.filter((i) => i.date === todayStr);

        if (todayItems.length === 0) return '<li>ei lounasta tänään</li>';

        return todayItems.map((i) => {
          const cal = i.calories ? ` (~${i.calories.value} kcal)` : '';
          return `<li>${i.name}${cal}</li>`;
        }).join('');
      })()
    : '<li>ei saatavilla</li>';
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${r.coordinates.lat},${r.coordinates.lng}`;
  const updatedAt = formatPopupUpdatedAt(r.updatedAt);

  return `
    <strong>${r.name}</strong><br/>
    <small>${r.address}</small><br/>
    <small>Etäisyys: ${distance} | Hinta: ${price}</small>
    <ul style="margin:4px 0;padding-left:16px;font-size:12px">${items}</ul>
    <small style="color:#666">Päivitetty: ${updatedAt}</small><br/>
    <a href="${mapsUrl}" target="_blank" rel="noopener noreferrer" style="display:inline-flex;align-items:center;gap:4px;font-size:12px;color:#1976d2;text-decoration:none;margin-top:4px;">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
      Navigoi
    </a>
  `;
}

function formatPopupUpdatedAt(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'juuri nyt';
  if (diffMins < 60) return `${diffMins} min sitten`;
  if (diffHours < 24) return `${diffHours} h sitten`;
  if (diffDays === 1) return 'eilen';
  if (diffDays < 7) return `${diffDays} pv sitten`;
  return date.toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric' });
}
